import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import { Progress } from '@llura/ui';
import { FlowSteps } from '@llura/constants';

import { useStepTiming } from '../../../lib/analytics';
import { useStrings } from '../../../hooks/useStrings';
import { GlowLogo } from '../../../shared/glow-logo';

interface GeneratingStepProps {
  onDone: () => void;
  durationMs?: number;
}

export function GeneratingStep({ onDone, durationMs = 2500 }: GeneratingStepProps) {
  useStepTiming(FlowSteps.CoachConfiguratorGenerating);
  const { strings } = useStrings();
  const phases = strings.onboarding.generating.phases;
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const startedAt = performance.now();
    let raf = 0;

    const tick = () => {
      const elapsed = performance.now() - startedAt;
      const pct = Math.min(100, (elapsed / durationMs) * 100);
      setProgress(pct);
      setPhase(Math.min(phases.length - 1, Math.floor((pct / 100) * phases.length)));
      if (pct < 100) raf = requestAnimationFrame(tick);
      else onDone();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [durationMs, onDone, phases.length]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="flex min-h-[60vh] flex-col items-center justify-center gap-10 text-center"
    >
      <GlowLogo size={176} />

      <div className="w-full max-w-xs space-y-3">
        <Progress value={progress} className="bg-muted" />
        <motion.p
          key={phase}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm text-muted-foreground"
        >
          {phases[phase]}
        </motion.p>
      </div>
    </motion.div>
  );
}
